import useI18n from '@/shared/hooks/usei18n'
import {
  Button,
  GenericFullscreenDialog,
  GoodsFamily,
  ReportingPeriodBasis
} from '@/common'
import ImportedGoodForm from '../components/importedGoodForm/ImportedGoodForm'
import {
  ImportedGoodFormData,
  validateImportedGoodForm
} from '../components/importedGoodForm/ImportedGoodForm.schema'
import { EMPTY_VALUE } from '../components/bulkUpdateSupplierEmissionsForm/BulkUpdateSupplierEmissionsForm.schema'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ImportedGood, useUpdateImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { toast } from 'react-hot-toast'
import gridEmissionFactorByCountry from '@/common/config/gridEmissionFactorByCountry'
import { getElectricityConsumption } from '@/shared/utils/getElectricityConsumption'
import { useCalculateDefaultLiability } from '@/features/cbam-reporting/hooks/cbamLiability'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'

export default function ImportedGoodEditDialog({
  isOpen,
  close,
  importedGood,
  goodsFamily,
  organizationId,
  isRepresentative,
  partnerships,
  installations,
  emissionsReports,
  reportingPeriodYear,
  periodId
}: {
  isOpen: boolean
  close: () => void
  importedGood: ImportedGood
  supplierName: string
  isRepresentative: boolean
  goodsFamily?: GoodsFamily
  basisForReportingPeriod: ReportingPeriodBasis
  installationName: string
  organizationId: string
  partnerships: Partnership[]
  installations: Installation[]
  emissionsReports: SupplierEmissionsReport[]
  reportingPeriodYear: number
  periodId: string
}) {
  const { t } = useI18n()
  const { data: goodsCodes } = useGoodsCodes({})

  const getAggregatedCategoryForCnCode = useMemo(() => {
    const byCnCode = new Map(
      (goodsCodes ?? []).map(goodsCode => [
        goodsCode.goodsCode,
        goodsCode.aggregatedGoodsCategory
      ])
    )
    return (cnCode: string) => byCnCode.get(cnCode)
  }, [goodsCodes])

  const { mutateAsync: updateImportedGood, isPending: isSaving } = useUpdateImportedGood(
    organizationId,
    importedGood.id
  )

  const [formData, setFormData] = useState<ImportedGoodFormData>({
    customId: '',
    importDate: '',
    description: '',
    cnCode: '',
    country: '',
    netMassInTonnes: 0,
    previousCustomsProcedure: '',
    requestedCustomsProcedure: '',
    inwardProcessingInformation: undefined,
    inwardProcessedNetMassInTonnes: undefined,
    isUsingDefaultEmissions: true,
    partnershipId: '',
    installationId: '',
    supplierEmissionsReportId: ''
  })

  // Derived values (EMPTY_VALUE means "clear" = no report = default emissions)
  const isUsingDefaultEmissions =
    !formData.supplierEmissionsReportId || formData.supplierEmissionsReportId === EMPTY_VALUE

  // Derive goodsFamily from currently selected installation (so CN code list updates when user changes installation)
  const selectedInstallation = useMemo(() => {
    if (!formData.installationId || formData.installationId === EMPTY_VALUE) {
      return undefined
    }
    return installations.find(inst => inst.id === formData.installationId)
  }, [formData.installationId, installations])

  const resetFormData = useCallback(() => {
    setFormData({
      importerId: importedGood.importerId || undefined,
      description: importedGood.description,
      cnCode: importedGood.cnCode,
      country: importedGood.country || '',
      customId: importedGood.customId || '',
      importDate: importedGood.importDate || '',
      netMassInTonnes: importedGood.netMassInTonnes,
      previousCustomsProcedure: importedGood.previousCustomsProcedure || '',
      requestedCustomsProcedure: importedGood.requestedCustomsProcedure || '',
      inwardProcessingInformation: importedGood.inwardProcessingInformation || undefined,
      inwardProcessedNetMassInTonnes: importedGood.inwardProcessedNetMassInTonnes || undefined,
      defaultSpecificDirectEmbeddedEmissions:
        importedGood.defaultSpecificDirectEmbeddedEmissions?.toString() || undefined,
      defaultSpecificIndirectEmbeddedEmissions:
        importedGood.defaultSpecificIndirectEmbeddedEmissions?.toString() || undefined,
      isUsingDefaultEmissions: importedGood.isUsingDefaultEmissions,
      partnershipId: importedGood.partnershipId || '',
      installationId: importedGood.installationId || '',
      supplierEmissionsReportId: importedGood.supplierEmissionsReportId || ''
    })
  }, [importedGood])

  useEffect(() => {
    resetFormData()
  }, [resetFormData, importedGood])

  const validationResult = useCallback(() => {
    return validateImportedGoodForm(formData)
  }, [formData])

  const isSaveDisabled = useMemo(() => {
    return isSaving || !validationResult().success
  }, [isSaving, validationResult])

  const onClose = useCallback(() => {
    // Reset form data to original imported good values when closing
    resetFormData()
    close()
  }, [close, resetFormData])

  const liabilityQuery = useCalculateDefaultLiability({
    code: formData.cnCode,
    country: formData.country,
    volume: '1' // We do not need the actual volume, we just need to calculate the liability for the default emissions
  })

  const liabilityData = liabilityQuery.data ?? null

  const onSave = useCallback(async () => {
    try {
      const validation = validationResult()

      if (!validation.success) {
        return
      }

      const electricityEmissionFactor =
        gridEmissionFactorByCountry[
          validation.data.country as keyof typeof gridEmissionFactorByCountry
        ] || 0

      const isUsingDefaultEmissions = validation.data.isUsingDefaultEmissions

      const defaultSpecificDirectEmbeddedEmissions = isUsingDefaultEmissions
        ? liabilityData?.projection[reportingPeriodYear]?.specificDirectEmbeddedEmissions || 0
        : undefined
      const defaultSpecificIndirectEmbeddedEmissions = isUsingDefaultEmissions
        ? liabilityData?.projection[reportingPeriodYear]?.specificIndirectEmbeddedEmissions || 0
        : undefined
      const electricityConsumption = isUsingDefaultEmissions
        ? getElectricityConsumption(
            Number(validation.data.defaultSpecificIndirectEmbeddedEmissions),
            Number(electricityEmissionFactor)
          )
        : undefined
      const defaultElectricityConsumptionMWh =
        electricityConsumption && Number.isFinite(electricityConsumption)
          ? electricityConsumption
          : undefined

      const installationId =
        validation.data.installationId && validation.data.installationId !== EMPTY_VALUE
          ? validation.data.installationId
          : undefined
      const partnershipId =
        validation.data.partnershipId && validation.data.partnershipId !== EMPTY_VALUE
          ? validation.data.partnershipId
          : undefined
      const supplierEmissionsReportId =
        validation.data.supplierEmissionsReportId &&
        validation.data.supplierEmissionsReportId !== EMPTY_VALUE
          ? validation.data.supplierEmissionsReportId
          : undefined

      await updateImportedGood({
        importerId: validation.data.importerId || undefined,
        customId: validation.data.customId || undefined,
        importDate: validation.data.importDate,
        cnCode: validation.data.cnCode,
        sellerName: importedGood.sellerName || undefined,
        country: validation.data.country,
        description: validation.data.description,
        netMassInTonnes: validation.data.netMassInTonnes,
        requestedCustomsProcedure: validation.data.requestedCustomsProcedure,
        isUsingDefaultEmissions: isUsingDefaultEmissions,
        previousCustomsProcedure: validation.data.previousCustomsProcedure,
        inwardProcessingInformation: validation.data.inwardProcessingInformation || undefined,
        inwardProcessedNetMassInTonnes: validation.data.inwardProcessedNetMassInTonnes || undefined,
        defaultSpecificDirectEmbeddedEmissions,
        defaultSpecificIndirectEmbeddedEmissions,
        defaultElectricityConsumptionMWh,
        installationId,
        partnershipId,
        supplierEmissionsReportId
      })
      toast.success(
        t(
          'cbamReporting:cbamReportDetails.importedGoodEditDialog.notification.importedGoodUpdatedSuccessfully'
        )
      )
      onClose()
    } catch (error) {
      console.error(error)
      toast.error(
        t(
          'cbamReporting:cbamReportDetails.importedGoodEditDialog.notification.failedToUpdateImportedGood'
        )
      )
    }
  }, [
    updateImportedGood,
    validationResult,
    onClose,
    t,
    liabilityData,
    importedGood.sellerName,
    reportingPeriodYear
  ])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamReporting:cbamReportDetails.importedGoodEditDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('cbamReporting:cbamReportDetails.importedGoodEditDialog.actions.save')}
          </Button>
        </div>
      }
    >
      <ImportedGoodForm
        organizationId={organizationId}
        data={formData}
        onChange={setFormData}
        isUsingDefaultEmissions={isUsingDefaultEmissions}
        goodsFamily={selectedInstallation?.goodsFamily ?? goodsFamily}
        isRepresentative={isRepresentative}
        getAggregatedCategoryForCnCode={getAggregatedCategoryForCnCode}
        partnerships={partnerships}
        installations={installations}
        emissionsReports={emissionsReports}
        hasSelectedInstallation={
          !!formData.installationId && formData.installationId !== EMPTY_VALUE
        }
        isEditMode={true}
        periodId={periodId}
        cbamReportYear={reportingPeriodYear}
      />
    </GenericFullscreenDialog>
  )
}
